import React, { useState, useEffect, useRef, useCallback, useMemo } from 'react';
import { useNavigate, useLocation } from 'react-router-dom';
import { useAuth } from '../context/AuthContext';
import { useHelpSearch } from '../hooks/useHelpSearch';
import { HELP_GROUPS, HELP_SECTIONS } from '../data/helpContent';
import { Collapsible, CollapsibleTrigger, CollapsibleContent } from '../components/ui/collapsible';
import axios from 'axios';
import {
    BookOpen,
    Search,
    Sparkles,
    ChevronDown,
    ChevronRight,
    ArrowLeft,
    Lightbulb,
    ExternalLink,
    Loader2,
    Menu,
    X,
} from 'lucide-react';

const API = `${process.env.REACT_APP_BACKEND_URL}/api`;

// ─── Sidebar ─────────────────────────────────────────────────────────────────

function SidebarSearch({ searchMode, setSearchMode, query, setQuery, results, onSelectResult, onAISubmit, aiLoading }) {
    const [showResults, setShowResults] = useState(false);
    const inputRef = useRef(null);
    const containerRef = useRef(null);

    useEffect(() => {
        function handleClickOutside(e) {
            if (containerRef.current && !containerRef.current.contains(e.target)) {
                setShowResults(false);
            }
        }
        document.addEventListener('mousedown', handleClickOutside);
        return () => document.removeEventListener('mousedown', handleClickOutside);
    }, []);

    const handleKeyDown = (e) => {
        if (e.key === 'Enter' && searchMode === 'ai' && query.trim()) {
            onAISubmit(query.trim());
        }
    };

    return (
        <div ref={containerRef} className="px-3 pb-3 space-y-2">
            {/* Mode toggle */}
            <div className="flex rounded-lg bg-stone-100 p-0.5">
                <button
                    onClick={() => setSearchMode('keyword')}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        searchMode === 'keyword'
                            ? 'bg-white text-stone-900 shadow-sm'
                            : 'text-stone-500 hover:text-stone-700'
                    }`}
                >
                    <Search className="w-3 h-3" />
                    Keyword
                </button>
                <button
                    onClick={() => setSearchMode('ai')}
                    className={`flex-1 flex items-center justify-center gap-1.5 px-3 py-1.5 rounded-md text-xs font-medium transition-colors ${
                        searchMode === 'ai'
                            ? 'bg-white text-stone-900 shadow-sm'
                            : 'text-stone-500 hover:text-stone-700'
                    }`}
                >
                    <Sparkles className="w-3 h-3" />
                    AI
                </button>
            </div>

            {/* Search input */}
            <div className="relative">
                <Search className="absolute left-2.5 top-1/2 -translate-y-1/2 w-3.5 h-3.5 text-stone-400" />
                <input
                    ref={inputRef}
                    type="text"
                    value={query}
                    onChange={(e) => {
                        setQuery(e.target.value);
                        if (searchMode === 'keyword') setShowResults(true);
                    }}
                    onFocus={() => { if (searchMode === 'keyword' && query.length >= 2) setShowResults(true); }}
                    onKeyDown={handleKeyDown}
                    placeholder={searchMode === 'ai' ? 'Ask a question...' : 'Search help...'}
                    className="w-full pl-8 pr-3 py-2 text-sm bg-stone-50 border border-stone-200 rounded-lg placeholder:text-stone-400 focus:outline-none focus:ring-2 focus:ring-stone-300 focus:border-stone-300"
                />
                {searchMode === 'ai' && query.trim() && (
                    <button
                        onClick={() => onAISubmit(query.trim())}
                        disabled={aiLoading}
                        className="absolute right-1.5 top-1/2 -translate-y-1/2 p-1 rounded-md bg-stone-900 text-white hover:bg-stone-700 disabled:opacity-50 transition-colors"
                    >
                        {aiLoading ? <Loader2 className="w-3 h-3 animate-spin" /> : <Sparkles className="w-3 h-3" />}
                    </button>
                )}
            </div>

            {/* Keyword results dropdown */}
            {searchMode === 'keyword' && showResults && results.length > 0 && (
                <div className="absolute z-50 left-3 right-3 mt-1 bg-white border border-stone-200 rounded-lg shadow-lg max-h-72 overflow-y-auto">
                    {results.map((r) => (
                        <button
                            key={r.item.id}
                            onClick={() => {
                                onSelectResult(r.item.slug);
                                setShowResults(false);
                            }}
                            className="w-full text-left px-3 py-2.5 hover:bg-stone-50 border-b border-stone-100 last:border-0 transition-colors"
                        >
                            <div className="text-sm font-medium text-stone-800">{r.item.title}</div>
                            <div className="text-xs text-stone-500 mt-0.5 line-clamp-1">{r.item.summary}</div>
                        </button>
                    ))}
                </div>
            )}
        </div>
    );
}

function SidebarTOC({ activeSection, onSelectSection }) {
    const grouped = useMemo(() => {
        const map = {};
        HELP_GROUPS.forEach(g => { map[g.id] = { ...g, sections: [] }; });
        HELP_SECTIONS.forEach(s => {
            if (map[s.group]) map[s.group].sections.push(s);
        });
        return HELP_GROUPS.map(g => map[g.id]).filter(g => g.sections.length > 0);
    }, []);

    const [openGroups, setOpenGroups] = useState(() => {
        const initial = {};
        HELP_GROUPS.forEach(g => { initial[g.id] = true; });
        return initial;
    });

    const toggleGroup = (id) => {
        setOpenGroups(prev => ({ ...prev, [id]: !prev[id] }));
    };

    return (
        <nav className="px-2 pb-4 space-y-1 overflow-y-auto flex-1">
            {grouped.map(group => (
                <Collapsible
                    key={group.id}
                    open={openGroups[group.id]}
                    onOpenChange={() => toggleGroup(group.id)}
                >
                    <CollapsibleTrigger className="flex items-center justify-between w-full px-2 py-1.5 text-xs font-semibold text-stone-500 uppercase tracking-wider hover:text-stone-700 transition-colors">
                        <span>{group.label}</span>
                        {openGroups[group.id] ? (
                            <ChevronDown className="w-3 h-3" />
                        ) : (
                            <ChevronRight className="w-3 h-3" />
                        )}
                    </CollapsibleTrigger>
                    <CollapsibleContent>
                        <div className="mt-0.5 space-y-0.5">
                            {group.sections.map(section => (
                                <button
                                    key={section.id}
                                    onClick={() => onSelectSection(section.slug)}
                                    className={`w-full text-left px-3 py-1.5 rounded-lg text-sm transition-colors ${
                                        activeSection === section.slug
                                            ? 'bg-stone-200 text-stone-900 font-medium'
                                            : 'text-stone-600 hover:bg-stone-100 hover:text-stone-800'
                                    }`}
                                >
                                    {section.title}
                                </button>
                            ))}
                        </div>
                    </CollapsibleContent>
                </Collapsible>
            ))}
        </nav>
    );
}

// ─── Content Renderers ───────────────────────────────────────────────────────

function ContentBlock({ block }) {
    switch (block.type) {
        case 'text':
            return <p className="text-sm text-stone-600 leading-relaxed">{block.body}</p>;

        case 'heading':
            return (
                <h3
                    className="text-base font-semibold text-stone-800 mt-4 mb-1"
                    style={{ fontFamily: 'Manrope, sans-serif' }}
                >
                    {block.text}
                </h3>
            );

        case 'screenshot':
            return (
                <figure className="my-3">
                    <img
                        src={block.src}
                        alt={block.alt || ''}
                        className="max-w-2xl w-full rounded-lg border border-stone-200 shadow-sm"
                        loading="lazy"
                        onError={(e) => { e.target.style.display = 'none'; }}
                    />
                    {block.caption && (
                        <figcaption className="text-xs text-stone-400 mt-1.5 text-center">
                            {block.caption}
                        </figcaption>
                    )}
                </figure>
            );

        case 'tip':
            return (
                <div className="flex gap-3 bg-amber-50 border-l-4 border-amber-400 rounded-r-lg px-4 py-3 my-3">
                    <Lightbulb className="w-4 h-4 text-amber-500 flex-shrink-0 mt-0.5" />
                    <p className="text-sm text-amber-900 leading-relaxed">{block.body}</p>
                </div>
            );

        case 'steps':
            return (
                <ol className="my-2 space-y-1.5 list-none pl-0">
                    {block.items.map((item, i) => (
                        <li key={i} className="flex gap-3 text-sm text-stone-600">
                            <span className="flex-shrink-0 w-5 h-5 rounded-full bg-stone-200 text-stone-700 text-xs font-semibold flex items-center justify-center mt-0.5">
                                {i + 1}
                            </span>
                            <span className="leading-relaxed">{item}</span>
                        </li>
                    ))}
                </ol>
            );

        default:
            return null;
    }
}

function SectionCard({ section, highlighted }) {
    return (
        <div
            id={`section-${section.slug}`}
            data-section={section.slug}
            className={`bg-white rounded-xl border border-stone-200 p-6 transition-shadow ${
                highlighted ? 'ring-2 ring-stone-400 shadow-md' : ''
            }`}
        >
            <div className="flex items-start gap-3 mb-4">
                <div className="flex-shrink-0 w-9 h-9 bg-stone-100 rounded-lg flex items-center justify-center">
                    <BookOpen className="w-4 h-4 text-stone-500" />
                </div>
                <div>
                    <h2
                        className="text-lg font-bold text-stone-900"
                        style={{ fontFamily: 'Manrope, sans-serif' }}
                    >
                        {section.title}
                    </h2>
                    <p className="text-xs text-stone-400 mt-0.5">{section.path}</p>
                </div>
            </div>
            <p className="text-sm text-stone-500 mb-4">{section.summary}</p>
            <div className="space-y-2">
                {section.content.map((block, i) => (
                    <ContentBlock key={i} block={block} />
                ))}
            </div>
        </div>
    );
}

function AIResponseCard({ response, loading }) {
    if (loading) {
        return (
            <div className="bg-white rounded-xl border border-stone-200 p-6 flex items-center gap-3">
                <Loader2 className="w-5 h-5 text-stone-400 animate-spin" />
                <span className="text-sm text-stone-500">Thinking...</span>
            </div>
        );
    }
    if (!response) return null;

    return (
        <div className="bg-white rounded-xl border border-purple-200 p-6">
            <div className="flex items-center gap-2 mb-3">
                <div className="w-7 h-7 rounded-lg bg-purple-100 flex items-center justify-center">
                    <Sparkles className="w-3.5 h-3.5 text-purple-600" />
                </div>
                <span className="text-sm font-semibold text-purple-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                    AI Answer
                </span>
            </div>
            <div className="text-sm text-stone-700 leading-relaxed whitespace-pre-wrap">
                {response}
            </div>
        </div>
    );
}

// ─── Main Page ───────────────────────────────────────────────────────────────

export default function HelpGuide() {
    const navigate = useNavigate();
    const location = useLocation();
    const { getAuthHeader } = useAuth();
    const { query, setQuery, results } = useHelpSearch();

    const [activeSection, setActiveSection] = useState('');
    const [highlightedSection, setHighlightedSection] = useState('');
    const [searchMode, setSearchMode] = useState('keyword');
    const [mobileSidebarOpen, setMobileSidebarOpen] = useState(false);

    // AI search state
    const [aiLoading, setAiLoading] = useState(false);
    const [aiResponse, setAiResponse] = useState(null);
    const [aiQuestion, setAiQuestion] = useState('');

    const sectionRefs = useRef({});
    const contentRef = useRef(null);
    const observerRef = useRef(null);

    // ─── Scroll to section ────────────────────────────────────────────────

    const scrollToSection = useCallback((slug) => {
        const el = document.getElementById(`section-${slug}`);
        if (el) {
            el.scrollIntoView({ behavior: 'smooth', block: 'start' });
            setHighlightedSection(slug);
            setTimeout(() => setHighlightedSection(''), 2000);
        }
        window.history.replaceState(null, '', `#${slug}`);
        setActiveSection(slug);
        setMobileSidebarOpen(false);
    }, []);

    // ─── Hash navigation on mount ─────────────────────────────────────────

    useEffect(() => {
        const hash = location.hash.replace('#', '');
        if (hash) {
            // Delay to ensure DOM is rendered
            const timer = setTimeout(() => scrollToSection(hash), 150);
            return () => clearTimeout(timer);
        }
    }, []); // eslint-disable-line react-hooks/exhaustive-deps

    // ─── Intersection Observer for active section ─────────────────────────

    useEffect(() => {
        if (observerRef.current) observerRef.current.disconnect();

        const observer = new IntersectionObserver(
            (entries) => {
                // Find the topmost visible section
                const visible = entries
                    .filter(e => e.isIntersecting)
                    .sort((a, b) => a.boundingClientRect.top - b.boundingClientRect.top);

                if (visible.length > 0) {
                    const slug = visible[0].target.getAttribute('data-section');
                    if (slug) {
                        setActiveSection(slug);
                        window.history.replaceState(null, '', `#${slug}`);
                    }
                }
            },
            {
                rootMargin: '-80px 0px -60% 0px',
                threshold: 0,
            }
        );

        observerRef.current = observer;

        // Observe all section cards
        const sectionEls = document.querySelectorAll('[data-section]');
        sectionEls.forEach(el => observer.observe(el));

        return () => observer.disconnect();
    }, []);

    // ─── AI Search ────────────────────────────────────────────────────────

    const handleAISubmit = useCallback(async (question) => {
        if (!question.trim()) return;
        setAiLoading(true);
        setAiResponse(null);
        setAiQuestion(question);
        try {
            const res = await axios.post(
                `${API}/help/ai-search`,
                { question },
                { headers: getAuthHeader() }
            );
            setAiResponse(res.data.answer || res.data.response || JSON.stringify(res.data));
        } catch (err) {
            setAiResponse('Sorry, I could not find an answer right now. Please try a keyword search or browse the sections below.');
        } finally {
            setAiLoading(false);
        }
    }, [getAuthHeader]);

    // ─── Sidebar content (shared between desktop and mobile) ──────────────

    const sidebarContent = (
        <>
            <div className="px-4 pt-4 pb-2">
                <div className="flex items-center gap-2 mb-3">
                    <BookOpen className="w-4 h-4 text-stone-500" />
                    <span className="text-sm font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                        Help Guide
                    </span>
                </div>
            </div>
            <SidebarSearch
                searchMode={searchMode}
                setSearchMode={setSearchMode}
                query={query}
                setQuery={setQuery}
                results={results}
                onSelectResult={(slug) => {
                    scrollToSection(slug);
                    setQuery('');
                }}
                onAISubmit={handleAISubmit}
                aiLoading={aiLoading}
            />
            <div className="border-t border-stone-200 mt-1" />
            <SidebarTOC activeSection={activeSection} onSelectSection={scrollToSection} />
        </>
    );

    return (
        <div className="min-h-screen bg-stone-50 flex">
            {/* Desktop sidebar */}
            <aside className="hidden lg:flex flex-col w-64 flex-shrink-0 border-r border-stone-200 bg-white h-screen sticky top-0 overflow-hidden">
                {sidebarContent}
            </aside>

            {/* Mobile sidebar overlay */}
            {mobileSidebarOpen && (
                <div className="fixed inset-0 z-50 lg:hidden">
                    <div
                        className="absolute inset-0 bg-black/30"
                        onClick={() => setMobileSidebarOpen(false)}
                    />
                    <aside className="relative w-72 max-w-[85vw] h-full bg-white flex flex-col shadow-xl overflow-hidden">
                        <div className="flex items-center justify-between px-4 pt-4">
                            <span className="text-sm font-bold text-stone-900" style={{ fontFamily: 'Manrope, sans-serif' }}>
                                Help Guide
                            </span>
                            <button
                                onClick={() => setMobileSidebarOpen(false)}
                                className="p-1 rounded-lg hover:bg-stone-100 text-stone-500"
                            >
                                <X className="w-4 h-4" />
                            </button>
                        </div>
                        <div className="mt-2" />
                        <SidebarSearch
                            searchMode={searchMode}
                            setSearchMode={setSearchMode}
                            query={query}
                            setQuery={setQuery}
                            results={results}
                            onSelectResult={(slug) => {
                                scrollToSection(slug);
                                setQuery('');
                            }}
                            onAISubmit={handleAISubmit}
                            aiLoading={aiLoading}
                        />
                        <div className="border-t border-stone-200 mt-1" />
                        <SidebarTOC activeSection={activeSection} onSelectSection={scrollToSection} />
                    </aside>
                </div>
            )}

            {/* Main content area */}
            <main ref={contentRef} className="flex-1 min-w-0">
                <div className="max-w-3xl mx-auto px-4 sm:px-6 py-6">
                    {/* Header */}
                    <div className="flex items-center gap-3 mb-6">
                        <button
                            onClick={() => navigate(-1)}
                            className="p-1.5 rounded-lg hover:bg-stone-200 text-stone-500 transition-colors"
                        >
                            <ArrowLeft className="w-4 h-4" />
                        </button>
                        <button
                            onClick={() => setMobileSidebarOpen(true)}
                            className="p-1.5 rounded-lg hover:bg-stone-200 text-stone-500 transition-colors lg:hidden"
                        >
                            <Menu className="w-4 h-4" />
                        </button>
                        <div>
                            <h1
                                className="text-2xl font-bold text-stone-900"
                                style={{ fontFamily: 'Manrope, sans-serif' }}
                            >
                                Help Guide
                            </h1>
                            <p className="text-sm text-stone-500 mt-0.5">
                                Learn how to use NurtureCRM
                            </p>
                        </div>
                    </div>

                    {/* AI response card */}
                    {(aiLoading || aiResponse) && (
                        <div className="mb-6">
                            {aiQuestion && (
                                <p className="text-xs text-stone-400 mb-2 flex items-center gap-1.5">
                                    <Sparkles className="w-3 h-3" />
                                    {aiQuestion}
                                </p>
                            )}
                            <AIResponseCard response={aiResponse} loading={aiLoading} />
                        </div>
                    )}

                    {/* Section cards */}
                    <div className="space-y-6">
                        {HELP_SECTIONS.map((section) => (
                            <SectionCard
                                key={section.id}
                                section={section}
                                highlighted={highlightedSection === section.slug}
                            />
                        ))}
                    </div>

                    {/* Footer */}
                    <div className="text-center mt-10 mb-6">
                        <p className="text-xs text-stone-400">
                            Can't find what you need?{' '}
                            <button
                                onClick={() => navigate('/feature-requests')}
                                className="text-stone-600 hover:text-stone-900 underline"
                            >
                                Submit a request
                            </button>
                        </p>
                    </div>
                </div>
            </main>
        </div>
    );
}
